import { FILTER_PLATFORM, PLATFORM } from '@/common/define/common.define';

import { PromiseAxiosResponse } from '@/worker/commands/config/apiInstance';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useViewModeStore } from '@/common/stores/view-mode';
import { FilterListData } from '@/types/common.types';
import { getLogTypeName } from '@/config/views/monitoring/log/utils/utils';
import { LogTargetType } from '@/config/views/monitoring/log/components/logTarget/logTypeSelect.define';
import { getV9Filters } from '@/api-client/common/queries/filter-controller/filter-controller';
import { getV8CloudAwsCostExplorerFilter } from '@/api-client/cloud/queries/cloud-filter-v8-controller/cloud-filter-v8-controller';
import { getV8LogFilters } from '@/api-client/log/queries/logging-view-v8-controller/logging-view-v8-controller';
import { getV6NotificationFilters } from '@/api-client/alert/queries/notification-history-controller/notification-history-controller';
import { getV9AlertsFilter } from '@/api-client/alert/queries/alert-filter-controller/alert-filter-controller';
import { getV9AlertEventFilter } from '@/api-client/alert/queries/event-alert-controller/event-alert-controller';
import { useVueQueryErrorHandler } from '../composables/useVueQueryErrorHandler';
import { getV1PreDefinedReportsHistoryFilters } from '@/api-client/report/queries/predefined-report-history-controller/predefined-report-history-controller';
import { getV1PreDefinedReportsExportExcel } from '@/api-client/report/queries/predefined-report-export-excel-controller/predefined-report-export-excel-controller';
import { getV8ReportExportFilter } from '@/api-client/report/queries/report-export-v8-controller/report-export-v8-controller';

export type PlatformType = (typeof PLATFORM)[keyof typeof PLATFORM];

export type ExtendPlatformType = (typeof FILTER_PLATFORM)[keyof typeof FILTER_PLATFORM] | '';

type FilterType = 'group' | 'target';

export interface FilterApiInfo {
  platform: ExtendPlatformType;
  menu?: string;
  type?: FilterType;
  frameName?: string;
}

export interface Filter {
  id: string;
  name: string;
}

export interface FilterData {
  key: Filter;
  values: FilterData[] | Filter[];
  type?: string;
  useSearch?: boolean;
  expand?: boolean;
}

type FilterControllerType = (request: any) => PromiseAxiosResponse<any>;

const NAME_MAP_MAPPER: Record<string, [string, string][]> = {
  // Filter List 카테고리명 매핑이 필요할 경우 이곳에 추가 후 하단 조건문에 조건식 추가
  'notification-history': [
    ['status', 'notification_status'],
    ['ruleType', 'notification_type'],
    ['group', 'alert_group'],
  ],
  npm: [['NetworkObjectGroup', 'network_object_group']],
  'rum-browser-error-tracking': [['rumGroupId', 'rum_group']],
  'rum-user-monitoring': [['rumGroupId', 'rum_group']],
  'page-performance-monitoring': [['rumGroupId', 'application']],
  'web-application': [['RumGroup', 'rum_group']],
  webserver: [
    ['wsmGroup', 'web_server_group'],
    ['webServerType', 'web_server_type'],
  ],
  url: [['URL Group', 'URLGROUPID']],
  'event-alert-list': [
    ['AlertType', 'alert_type'],
    ['AlertGroup', 'alert_group'],
    ['AlertLevel', 'alert_level'],
  ],
  'fixed-export': [['Template Report Group', 'Template Report List']],
};

const REQUIRED_GROUP_PERMISSION_MENUS = [
  'cluster-topology',
  'cluster',
  'node',
  'pv',
  'sc',
  'cluster-role',
  'cluster-role-binding',
];
export const isFilterData = (v: FilterData | Filter): v is FilterData =>
  typeof v === 'object' && 'values' in v && 'key' in v;
export const isFilter = (v: FilterData | Filter): v is Filter =>
  typeof v === 'object' && 'id' in v && 'name' in v;

/**
 * @param checkedFilterListData checkedFilterListData
 * @returns Record<string, string[]>
 * @description checkedFilterListData에서 top level key.id를 key로, nested한 id를 values로 하는 Record를 반환합니다.
 */
export const extractFilterIds = (checkedFilterListData: FilterData[]): Record<string, string[]> => {
  const extract = (data: FilterData[] | Filter[]) => {
    const flatIds: string[] = [];

    data.forEach((d: FilterData | Filter) => {
      if (isFilter(d)) {
        flatIds.push(d.id);
      } else if (isFilterData(d)) {
        flatIds.push(...extract(d.values));
      }
    });

    return flatIds;
  };

  const result = {};
  const keys = checkedFilterListData.map((f) => f.key.id);

  keys.forEach((k) => {
    result[k] = extract(checkedFilterListData.find((f) => f.key.id === k)?.values ?? []);
  });
  return result;
};

/**
 * GPU 모델 카테고리 판정 — `/v9/filters?platform=gpu` 응답의 `Cluster GPU Model Name` /
 * `Host GPU Model Name` 류를 가리킨다. 스토어(섹션 정렬·표시명 통일)와 소비 화면(조회 파라미터 매핑)이
 * **같은 기준**을 쓰도록 여기서만 정의한다. 양쪽이 각자 판정하면 BE가 라벨을 바꿀 때 한쪽만 어긋나
 * 모델 필터가 조용히 무력화된다.
 */
export const isGpuModelCategoryId = (id?: string): boolean => /gpu\s*model/i.test(id ?? '');

export const isGroupedFilterItem = (
  item: any,
): item is { groupId: string; groupName: string; values: any[] } => {
  return (
    item &&
    typeof item === 'object' &&
    'groupId' in item &&
    'groupName' in item &&
    Array.isArray(item.values)
  );
};

const getFilterController = (
  platform: ExtendPlatformType,
  menu?: string,
): FilterControllerType | null => {
  if (!platform) return null;

  switch (platform) {
    case 'cost-explorer':
      return getV8CloudAwsCostExplorerFilter;
    case 'logging':
      return getV8LogFilters;
    case 'alert':
      switch (menu) {
        case 'alert-list':
          return getV9AlertsFilter;
        case 'event-alert-list':
          return getV9AlertEventFilter;
        case 'notification-history':
          return getV6NotificationFilters;
        default:
          return null;
      }
    case 'dashboardExport':
      return getV8ReportExportFilter;
    case 'predefinedReport':
      return getV1PreDefinedReportsHistoryFilters;
    case 'predefinedReportExport':
      return getV1PreDefinedReportsExportExcel;
    default:
      return getV9Filters;
  }
};

const getFilterParams = ({
  platform,
  type,
  menu = '',
}: {
  platform: ExtendPlatformType;
  type?: FilterType;
  menu?: string;
}) => {
  switch (platform) {
    case 'cost-explorer':
      return {
        cloud: 'aws',
        menu: 'cost-explorer',
      };
    default:
      return {
        platform,
        type,
        requiredGroupPermission: REQUIRED_GROUP_PERMISSION_MENUS.includes(menu),
      };
  }
};

const getLogFilterData = (filterRawData) => {
  const logTypeFilter = filterRawData.find((filter) => filter.key?.id === 'logType');
  if (logTypeFilter) {
    logTypeFilter.values = logTypeFilter.values.map((value) => ({
      id: value.id,
      name: value.id === 'custom' ? 'Custom (Host)' : getLogTypeName(value.id as LogTargetType),
    }));
  }
  return filterRawData;
};

const processFilterData = ({
  platform,
  menu,
  rawData,
}: {
  platform: ExtendPlatformType;
  menu: string;
  rawData: any[];
}) => {
  const NameMap = new Map(NAME_MAP_MAPPER[menu] ?? []);

  if (platform === 'host') {
    return rawData.length > 1 ? [{ ...rawData[0], useSearch: true }, rawData[1]] : [];
  }
  if (platform === 'container_host') {
    return rawData.map((filter: FilterData) => ({
      key: {
        id: filter.key?.id ?? '',
        name: NameMap.get(filter.key?.name ?? '') ?? filter.key?.name ?? '',
      },
      values: (filter.values as FilterData[]).map(({ key, values }) => ({
        groupId: key.id,
        groupName: key.name,
        values: [...values],
      })),
    }));
  }

  // GPU: 응답 카테고리(Cluster / Host Group / Cluster·Host GPU Model Name)를 그대로 사용한다.
  // 카테고리 id가 곧 조회 파라미터 매핑 기준(gpuMonitoringView buildGpuListParams)이라 id는 건드리지 않는다.
  // 모델명은 /v11/infra/gpu/models 별도 조회 → v9/filters 응답 포함으로 이관됨(해당 API deprecated).
  if (platform === 'gpu') {
    // GPU 유형만 응답에 없어 화면에서 고정 항목으로 제공한다 (gpu/list 의 gpuTypes 파라미터).
    const gpuTypeSection = {
      key: { id: 'gpuType', name: 'GPU Type' },
      values: [
        { id: 'gpu', name: 'GPU' },
        { id: 'mig', name: 'MIG Instance' },
      ],
    };

    // 그룹(Cluster/Host Group) → GPU 유형 → 모델 순으로 노출 (응답은 그룹·모델이 교차 순서로 온다).
    // 모델은 플랫폼별로 하나만 노출되므로 표시명을 'GPU Model'로 통일한다(id는 유지).
    const isModelCategory = (filter: FilterData) => isGpuModelCategoryId(filter.key?.id);
    return [
      ...rawData.filter((filter) => !isModelCategory(filter)),
      gpuTypeSection,
      ...rawData
        .filter(isModelCategory)
        .map((filter) => ({ ...filter, key: { ...filter.key, name: 'GPU Model' } })),
    ];
  }

  if (menu === 'logging') {
    return getLogFilterData(rawData);
  }

  if (platform === 'predefinedReport' || platform === 'predefinedReportExport') {
    return [
      {
        key: { id: 'report', name: 'Template Report List' },
        values: rawData.map((item: { reportId?: number; reportName?: string }) => ({
          id: String(item.reportId ?? ''),
          name: item.reportName ?? '',
        })),
      },
    ];
  }

  return rawData.map((filter) => ({
    ...filter,
    key: {
      id: filter.key?.id ?? '',
      name: NameMap.get(filter.key?.name ?? '') ?? filter.key?.name ?? '',
    },
  }));
};

export const useFilterListStore = defineStore('filterList', () => {
  const filterListData = ref<FilterListData[]>([]);
  const checkedFilterListData = ref<FilterData[]>([]);
  const isFilterLoading = ref(false);

  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const fetchFilterList = async (parameter) => {
    filterListData.value = [];
    const { platform, type, menu } = parameter;

    const controller = getFilterController(platform, menu);
    if (!controller) return;

    isFilterLoading.value = true;

    const params = getFilterParams({ platform, type, menu });

    try {
      const response = await controller(params);

      let rawData = response.data?.data ?? [];

      // 사실은 API에서 막는게 맞는데... 일단 화면에서도 처리.
      if (menu === 'alert-list' && useViewModeStore().isMaxgaugeMode) {
        rawData = rawData.map((item: any) => ({
          ...item,
          values: Array.isArray(item?.values)
            ? item.values.filter((v: any) => v?.key?.id !== 'External Alert Group')
            : item?.values,
        }));
      }

      filterListData.value = processFilterData({
        platform,
        menu,
        rawData,
      });
    } catch (e) {
      errorHandlerWithMessage({ error: e });
    } finally {
      isFilterLoading.value = false;
    }
  };

  const updateCheckedFilterList = (checkedItems: FilterData[]) => {
    checkedFilterListData.value = checkedItems;
  };

  return {
    filterListData,
    checkedFilterListData,
    isFilterLoading,
    fetchFilterList,
    updateCheckedFilterList,
  };
});
